import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import {
  BorderedBoxIcon,
  CloseIcon,
  NextIcon,
  PlusIcon,
  PrevIcon,
} from '../../icons/icons';
import TextBoxGroup from '../../text-box-group/text-box-group';
import SwitchGroup from '../../switch-group/switch-group';
import Button from '../../button/button';
import SearchSelect from '../../search-select/search-select';
import { getBach, handlePrdPkgChange, isGrossWtInvalid } from './buyerHelper';
import Action from '../../action/action';
import Checkbox from '../../custom-checkbox/custom-checkbox';

function PackagingDetailsTab({
  setSupplierTab,
  setTabDetails,
  packages,
  setPackages,
  products,
  tabDetails,
  packageOptions,
}) {
  const [t] = useTranslation('manageorders');

  const packageListForDD = (currentPkg) => {
    return packageOptions?.map((opt) => {
      const isPkgSelectedByOthers = packages?.some?.((p) => {
        if (p?.id !== currentPkg?.id && opt?.value === p?.name) {
          return true;
        }
        return false;
      });
      if (!isPkgSelectedByOthers) {
        return {
          label: opt?.label,
          value: opt?.value,
        };
      }
      return null;
    });
  };

  const productsListForDD = (currentPkg) => {
    return products
      ?.map((prd) => {
        const isPrdSelectedByOtherPkg = packages?.some?.((p) => {
          if (
            p?.id !== currentPkg?.id &&
            p?.selectedProductIds?.includes(prd?.id)
          ) {
            return true;
          }
          return false;
        });
        if (prd?.name && !isPrdSelectedByOtherPkg) {
          return {
            id: prd?.id,
            label: prd?.name,
            value: prd?.name,
          };
        }
        return null;
      })
      .filter(Boolean);
  };

  const handleAddAnotherProduct = useCallback(() => {
    let newId = 1;
    if (packages?.length) {
      newId = packages[packages.length - 1].id + 1 || 1;
    }
    const newPackage = {
      id: newId,
      name: '',
      quantity: null,
      length: null,
      width: null,
      height: null,
      grossWeight: null,
      measurementUnit: 'metric',
      selectedProductIds: [],
    };
    setPackages((prev) => [...prev, newPackage]);
  }, [packages, setPackages]);

  const validatePackages = () => {
    const isPackageValid = (pkg) => {
      const {
        name,
        quantity,
        length,
        width,
        height,
        grossWeight,
        measurementUnit,
        selectedProductIds,
      } = pkg;

      return (
        name?.trim() &&
        quantity &&
        length &&
        width &&
        height &&
        grossWeight &&
        measurementUnit &&
        selectedProductIds.length > 0
      );
    };
    const isValid = packages
      .filter((pkg) => pkg.name !== 'Default Package')
      .every((pkg) => isPackageValid(pkg) && !isGrossWtInvalid(pkg, products));
    return {
      isValid,
      errorMessage: isValid
        ? null
        : 'At least one package must have all fields filled, including at least one selected product.',
    };
  };

  const areAllPrdsSelected = () => {
    let productCount = 0;
    packages?.forEach((pkg) => {
      productCount += pkg.selectedProductIds.length;
    });
    return productCount === products?.length;
  };

  useEffect(() => {
    setTabDetails((prev) => {
      const updatedTabDetails = { ...prev };
      if (validatePackages()?.isValid && areAllPrdsSelected()) {
        updatedTabDetails.disableShipDetails = false;
      } else {
        updatedTabDetails.disableShipDetails = true;
        updatedTabDetails.disableNxtShip = true;
      }
      return updatedTabDetails;
    });
  }, [validatePackages()?.isValid, areAllPrdsSelected()]);

  const deletePackage = (id) => {
    if (packages?.length > 1) {
      setPackages((prev) => {
        const updatedPackage = prev?.filter((pkg) => {
          if (pkg?.id !== id) {
            return true;
          }
          return false;
        });
        return updatedPackage;
      });
    }
  };

  return (
    <div className="supplier-booking-content ask-qut">
      <h3>{t('manageordersscreen.packagingdetails.goodsinformation')}</h3>

      <div className="package-details-outerwrapper">
        <div className="d-flex justify-between">
          <h3 className="m-0">
            {t('manageordersscreen.packagingdetails.createpackages')}
          </h3>
        </div>

        {packages?.map((pkg) => {
          const translatedName = packageOptions?.find(
            (opt) => opt?.value === pkg?.name
          )?.label;
          return (
            <div className="package-details-wrapper">
              <div className="package-details-card multiple">
                <div className="heading">
                  <div className="left">
                    <span className="card-icon">
                      <BorderedBoxIcon />
                    </span>
                    <table>
                      <tr>
                        <td>
                          <SearchSelect
                            t={t}
                            data={packageListForDD(pkg)}
                            onChange={(e) => {
                              e.target.name = 'name';
                              handlePrdPkgChange(e, pkg?.id, setPackages);
                            }}
                            label={t(
                              'manageordersscreen.packagingdetails.selectpackage'
                            )}
                            defaultValue={translatedName || pkg?.name}
                            selectProduct={tabDetails?.packagingDetails}
                            showFooterMessage={false}
                            containerClassName="min-w-200"
                            selectStyle
                            enableRemove
                          />
                        </td>
                      </tr>
                    </table>

                    <SwitchGroup
                      name="measurementUnit"
                      defaultSelected={0}
                      onChange={(e) => {
                        handlePrdPkgChange(e, pkg?.id, setPackages);
                      }}
                      disabled={!pkg?.name || pkg?.name === 'Default Package'}
                    >
                      <span
                        className={
                          pkg?.measurementUnit === 'metric' ? '' : 'inactive'
                        }
                      >
                        {t('manageordersscreen.packagingdetails.metric')}
                      </span>
                      <span
                        className={
                          pkg?.measurementUnit === 'imperial' ? '' : 'inactive'
                        }
                      >
                        {t('manageordersscreen.packagingdetails.imperial')}
                      </span>
                    </SwitchGroup>
                  </div>
                  {packages?.length > 1 && (
                    <div className="right">
                      <Button
                        styles="icon"
                        icon={<CloseIcon />}
                        onClick={() => deletePackage(pkg?.id)}
                      />
                    </div>
                  )}
                </div>
                <div className="body">
                  <table>
                    <thead>
                      <tr>
                        {[
                          'quantity',
                          'length',
                          'width',
                          'height',
                          'grossWeight',
                          'products',
                        ].map((field) => (
                          <th>
                            {t(`manageordersscreen.packagingdetails.${field}`)}
                            {pkg?.name !== 'Default Package' && (
                              <span className="required">*</span>
                            )}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        {[
                          'quantity',
                          'length',
                          'width',
                          'height',
                          'grossWeight',
                        ].map((field) => (
                          <td key={field}>
                            <TextBoxGroup
                              id={field}
                              name={field}
                              type="number"
                              placeholder={t(
                                `manageordersscreen.packagingdetails.${field}`
                              )}
                              bach={getBach(pkg, field)}
                              onChange={(e) =>
                                handlePrdPkgChange(e, pkg?.id, setPackages)
                              }
                              disabled={
                                !pkg?.name || pkg?.name === 'Default Package'
                              }
                              value={
                                pkg?.name === 'Default Package'
                                  ? ''
                                  : pkg?.[field] || ''
                              }
                              onPaste={(e) => {
                                e.preventDefault(); // Prevent the default paste action
                                const pastedValue =
                                  e.clipboardData.getData('text');
                                if (/^\d*\.?\d*$/.test(pastedValue)) {
                                  const [integerPart, decimalPart] =
                                    pastedValue.split('.');
                                  const sanitizedValue = decimalPart
                                    ? `${integerPart}.${decimalPart.slice(
                                        0,
                                        2
                                      )}`
                                    : integerPart;
                                  e.target.value =
                                    field === 'quantity'
                                      ? integerPart
                                      : sanitizedValue;
                                  e.target.dispatchEvent(
                                    new Event('input', { bubbles: true })
                                  ); // Trigger React change
                                  handlePrdPkgChange(e, pkg?.id, setPackages);
                                }
                              }}
                              errorMessage={
                                field === 'grossWeight' &&
                                pkg?.grossWeight &&
                                isGrossWtInvalid(pkg, products) &&
                                t(
                                  'manageordersscreen.packagingdetails.grossWtMsg'
                                )
                              }
                            />
                          </td>
                        ))}
                        <td>
                          <Action
                            label={
                              pkg?.selectedProductIds?.length
                                ? `${pkg?.selectedProductIds?.length} ${
                                    pkg?.selectedProductIds?.length > 1
                                      ? t(
                                          'manageordersscreen.sendbookingdetails.addpackagedetailsbtn.productsplaceholder'
                                        )
                                      : t(
                                          'manageordersscreen.sendbookingdetails.addpackagedetailsbtn.productplaceholder'
                                        )
                                  }`
                                : t(
                                    'manageordersscreen.sendbookingdetails.addpackagedetailsbtn.productsplaceholdernopackage'
                                  )
                            }
                            className="select-box"
                            required
                            disabled={!pkg?.name}
                          >
                            {productsListForDD(pkg)?.length &&
                              productsListForDD(pkg)?.map((child) => {
                                return (
                                  <Checkbox
                                    key={child?.id}
                                    value={child?.value}
                                    label={child?.label}
                                    className={
                                      child?.label.length > 15 ? 'small' : ''
                                    }
                                    onChange={(e) => {
                                      e.target.name = 'selectedProductIds';
                                      e.target.productId = child?.id;
                                      handlePrdPkgChange(
                                        e,
                                        pkg?.id,
                                        setPackages
                                      );
                                    }}
                                    checked={pkg?.selectedProductIds?.includes(
                                      child?.id
                                    )}
                                  />
                                );
                              })}
                            <p className="popover-small-text">
                              {t(
                                'manageordersscreen.packagingdetails.cantfindproduct'
                              )}{' '}
                              {t(
                                'manageordersscreen.packagingdetails.dontworry'
                              )}{' '}
                              <button
                                type="button"
                                onClick={() =>
                                  setSupplierTab('product details')
                                }
                              >
                                {t(
                                  'manageordersscreen.packagingdetails.previousStep'
                                )}
                              </button>
                            </p>
                          </Action>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          );
        })}

        <div className="supplier-booking-button-area justify-between">
          <button
            type="button"
            className="add-more"
            onClick={handleAddAnotherProduct}
          >
            <PlusIcon color="#0088fa" />
            {t('manageordersscreen.packagingdetails.addanotherpackage')}
          </button>
        </div>
      </div>

      <div className="supplier-booking-button-area justify-between mt-10">
        <Button
          label={t('manageordersscreen.createbookingmenu.confirmentities.back')}
          styles="back"
          size="large"
          icon={<PrevIcon />}
          spacing="0.694vw"
          className="px-24"
          onClick={() => {
            setSupplierTab('product details');
          }}
        />
        <Button
          label={t('manageordersscreen.createbookingmenu.confirmentities.next')}
          styles="submit"
          size="large"
          onClick={() => {
            if (areAllPrdsSelected()) {
              setTabDetails((prev) => {
                const updatedTabDetails = { ...prev };
                updatedTabDetails.packagingDetails = true;
                return updatedTabDetails;
              });
              setSupplierTab('shipment details');
            } else {
              toast.error(
                t('manageordersscreen.packagingdetails.selectproductwarning')
              );
            }
          }}
          iconRight={<NextIcon />}
          spacing="0.694vw"
          className="px-24"
          disabled={!validatePackages()?.isValid}
        />
      </div>
    </div>
  );
}

export default React.memo(PackagingDetailsTab);

PackagingDetailsTab.propTypes = {
  setSupplierTab: PropTypes.func,
  setTabDetails: PropTypes.func,
  packages: PropTypes.instanceOf(Array),
  setPackages: PropTypes.func,
  products: PropTypes.instanceOf(Array),
  tabDetails: PropTypes.instanceOf(Object),
  packageOptions: PropTypes.instanceOf(Array),
};

PackagingDetailsTab.defaultProps = {
  setSupplierTab: () => {},
  setTabDetails: () => {},
  packages: [],
  setPackages: () => {},
  products: [],
  tabDetails: {},
  packageOptions: [],
};
